import React, { useEffect, useRef, useState } from "react";
import CustomSpinner from "../../../../util/CustomSpinner";
import DataTableComponent, { ColumnOption } from "../../../common/DataTable";
import axios from "../../../../util/axiosconfig";
import { Toast } from "primereact/toast";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProgressBar } from "primereact/progressbar";
import { Error } from "../../../../util/types";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { ExportHistoryIcon } from "../../../layout/sidebar/SideBarSVGs";

interface ExportHistory {
  id: number;
  file_name: string;
  status: string;
  file_location: string;
  created_at: string; // ISO string
}

type FetchedInvoices = {
  id: number;
  file_name: string;
  status: string;
  file_location: string;
  created_at: string;
  import_name: string;
  import_type: string;
  processed_batches: number;
  total_batches: number;
  total_records: number;
};

const InvoiceExportHistory: React.FC = () => {
  const [invoices, setInvoices] = useState<ExportHistory[]>([]);
  const [downloadInvoiceExportId, setDownloadInvoiceExportId] = useState<
    number | null
  >(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [firstTimeLoading, setFirstTimeLoading] = useState<boolean>(true);
  const [changePageLoading, setChangePageLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [size, setSize] = useState(10);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  let page = 0;

  const fetchInvoiceHistory = async (page = 0, size = 10) => {
    // if (stopPolling.current) return; // Prevent further API calls

    const params = { page, size, importType: "export-invoices" };

    setLoading(true);
    try {
      const response = await axios.get("user/download-history", { params });
      const fetchedInvoices = response.data.result;
      setChangePageLoading(false);
      if (!fetchedInvoices) {
        showError(
          response?.data?.Message ?? "Failed to fetch invoice history.",
          toast,
          null,
          8000
        );
        // stopPolling.current = true;
      }
      setInvoices(fetchedInvoices);
      setTotalRecords(response.data.totalCount);
      setLoading(false);
      setChangePageLoading(false);
      setFirstTimeLoading(false);

      const allCompleted = fetchedInvoices?.every(
        (invoice: FetchedInvoices) => invoice.status === "Completed"
      );
      if (allCompleted && intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    } catch (err: unknown) {
      const error = err as Error;
      showError(
        error?.response?.data?.Message ?? "Failed to fetch invoice history.",
        toast
      );
      setLoading(false);
      setChangePageLoading(false);
      // stopPolling.current = true; // Stop further API calls
    }
  };

  // useEffect(() => {
  //   fetchInvoiceHistory(page, size);
  //   intervalIdRef.current = setInterval(
  //     () => fetchInvoiceHistory(page, size),
  //     2800
  //   );

  //   return () => {
  //     if (intervalIdRef.current) {
  //       clearInterval(intervalIdRef.current);
  //     }
  //   };
  // }, []);

  // const onPageChange = (event: PaginatorPageChangeEvent) => {
  //   setChangePageLoading(true);
  //   setFirst(event.first);
  //   setSize(event.rows);
  //   const currentPage = Math.floor(event.first / event.rows); // Calculate the current page index
  //   fetchInvoiceHistory(currentPage, event.rows);
  // };

  useEffect(() => {
    fetchInvoiceHistory(page, size);
    const startPolling = () => {
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(() => {
          fetchInvoiceHistory(page, size);
        }, 2800);
      }
    };

    const stopPolling = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    // Start polling on mount
    startPolling();

    return () => {
      stopPolling(); // Stop polling on component unmount
    };
  }, [page, size]);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setChangePageLoading(true);
    setFirst(event.first);
    setSize(event.rows);
    const currentPage = Math.floor(event.first / event.rows); // Calculate the current page index
    fetchInvoiceHistory(currentPage, event.rows);

    // Update page reference
    page = currentPage;

    // Restart polling with updated values
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    intervalIdRef.current = setInterval(() => {
      fetchInvoiceHistory(currentPage, event.rows);
    }, 2800);
  };

  const handleDownload = async (invoice: FetchedInvoices) => {
    setIsButtonDisabled(true);
    setIsDownloading(true);
    setDownloadInvoiceExportId(invoice.id);
    try {
      const url = invoice.file_location;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        invoice.file_location.split("/").pop() || "download.zip"
      );
      document.body.appendChild(link);
      link.click();
      setIsButtonDisabled(false);
      showSuccess(`${invoice.file_name} downloaded successfully.`, toast);
    } catch (err: unknown) {
      const error = err as Error;
      showError(
        error?.response?.data?.Message ?? "Failed to download invoice.",
        toast
      );
      setIsButtonDisabled(false);
    } finally {
      setIsButtonDisabled(false);
      setIsDownloading(false);
      setDownloadInvoiceExportId(null);
    }
  };

  const downloadBtnBody = (invoice: FetchedInvoices) => {
    const isInProgress = invoice.status === "In Progress";
    return (
      <div className="tw-flex tw-items-center tw-relative">
        {isInProgress ? (
          <ProgressBar
            mode="indeterminate"
            style={{ height: "6px", width: "97px" }} // Ensure proper dimensions
            color="#2a4870"
          />
        ) : (
          <Button
            onClick={() => handleDownload(invoice)}
            disabled={isButtonDisabled || invoice.status === "Failed"}
            className={`tw-px-3 tw-py-1 tw-rounded focus:tw-shadow-none tw-bg-[#2a4870] tw-text-white hover:tw-bg-[#182c44]`}
          >
            Download
          </Button>
        )}
        {isDownloading && downloadInvoiceExportId === invoice.id && (
          <CustomSpinner spinnerColour="tw-absolute tw-ml-[115px] tw-border-t-[#182c44]" />
        )}
      </div>
    );
  };

  const exportHistoryColumns: ColumnOption[] = [
    {
      field: "file_name",
      header: "File Name",
      visible: true,
    },
    {
      field: "created_at",
      header: "Created At",
      body: (rowData: FetchedInvoices) => {
        const date = new Date(rowData.created_at);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`; // Format: dd-mm-yyyy hh:mm:ss
      },
      visible: true,
    },
    {
      field: "status",
      header: "Status",
      visible: true,
    },
    {
      field: "downloadedBtn",
      header: "Actions",
      body: downloadBtnBody,
      visible: true,
    },
  ];

  return (
    <div className="tw-p-4 tw-min-h-screen">
      <Toast ref={toast} />

      <div className="tw-flex tw-flex-column tw-bg-gray-50 tw-pt-4 tw-pl-4">
        <ExportHistoryIcon fill={"#000000"} />
        <h1 className="tw-text-2xl tw-font-bold tw-ml-1 tw-mt-[3px]">
          Invoice Export History
        </h1>
      </div>

      {changePageLoading && (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-mt-[-100px] tw-ml-[80px] tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
          <ProgressSpinner />
        </div>
      )}

      {loading && firstTimeLoading ? (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-mt-[-100px] tw-ml-[80px] tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="tw-overflow-x-auto">
          <DataTableComponent
            rowData={invoices}
            columns={exportHistoryColumns}
          />
          <Paginator
            first={first}
            rows={size}
            totalRecords={totalRecords}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceExportHistory;
